import { useState } from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { useDebounce } from "hooks/useDebounce";

export const useDrugFavourites = () => {
  const queryClient = useQueryClient();

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const [drugSearchQuery, setDrugSearchQuery] = useState("");
  const debouncedQuery = useDebounce(drugSearchQuery, 300);

  const { data: drugFavourites = [], isPending: drugFavouritesLoading } =
    useQuery({
      queryKey: ["drugFavourites", tenantVal],
      queryFn: async () => {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}/drugsfile/favourite`
        );
        return response.data || [];
      },
      enabled: !!tenantVal,
    });

  const { data: drugSearchResults = [], isFetching: drugSearchLoading } =
    useQuery({
      queryKey: ["drugSearch", tenantVal, debouncedQuery],
      queryFn: async () => {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `${tenantVal}/drugsfile/search/${encodeURIComponent(debouncedQuery)}`
        );
        return response.data || [];
      },
      enabled: !!tenantVal && debouncedQuery.length >= 2,
    });

  const { mutate: addDrugFavourite, isPending: addingFavourite } = useMutation(
    {
      mutationFn: async (drug) => {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.post(`${tenantVal}/drugsfile/favourite`, {
          drugsFileId: drug.id,
        });
        return drug;
      },
      onSuccess: (drug) => {
        toast.success(
          `${drug.tradeName || drug.genericName} added to favourites`
        );
        setDrugSearchQuery("");
        queryClient.invalidateQueries({
          queryKey: ["drugFavourites", tenantVal],
        });
      },
      onError: () => toast.error("Failed to add drug to favourites"),
    }
  );

  const { mutate: removeDrugFavourite } = useMutation({
    mutationFn: async (favourite) => {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.delete(`${tenantVal}/drugsfile/favourite`, {
        data: { drugsFileId: favourite.id },
      });
    },
    onSuccess: () => {
      toast.success("Drug removed from favourites");
      queryClient.invalidateQueries({
        queryKey: ["drugFavourites", tenantVal],
      });
    },
    onError: () => toast.error("Failed to remove drug from favourites"),
  });

  return {
    drugFavourites,
    drugFavouritesLoading,
    drugSearchQuery,
    setDrugSearchQuery,
    drugSearchResults,
    drugSearchLoading,
    addingFavourite,
    addDrugFavourite,
    removeDrugFavourite,
  };
};
